import React, { useState, useEffect } from "react";
import * as Mui from "@mui/material";
import AccessTimeRounded from "@mui/icons-material/AccessTimeRounded";
import AddCircleOutlineRounded from "@mui/icons-material/AddCircleOutlineRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import LinkRounded from "@mui/icons-material/LinkRounded";
import PlaceRounded from "@mui/icons-material/PlaceRounded";
import RemoveCircleOutlineRounded from "@mui/icons-material/RemoveCircleOutlineRounded";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { customAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { useFetch } from "services/hooks/useFetch";
import { defaultAllSelection } from "services/constants";
import { tooltipStyleProps } from "services/constants/Styles";
import { designColors, designShell } from "services/constants/designTokens";
import {
  GlobalConfirmDialog,
  globalTextFieldSx,
} from "components/DesignSystem/sharedUiPatterns";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import {
  PanelActionFooter,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import { CustomAutoComplete } from "components/CustomComponents";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { CustomTimePicker } from "components/CustomComponents/CustomTimePicker";
import { Loader } from "components/Loader";
import { scopeName } from "pages/Administration/AdministrationHeaders";
import {
  IconFieldBlock,
  PanelFieldText,
  PanelIconHeader,
  PanelSectionCard,
  ToggleRow,
} from "../settingsUi";

const FORM_ID = "check-in-policy-form";

// The switches below sit inside PanelSectionCards (1px border + 16px side
// padding). A switch placed outside a card is inset by the same amount so
// every switch in the panel lines up in one vertical column.
const CARD_SWITCH_INSET = "17px";

// Trip distance threshold: 10 to 100 metres, in steps of 5.
const MIN = 10;
const MAX = 100;
const STEP = 5;
const snapDistance = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number) || value === "") return 20;
  return Math.min(MAX, Math.max(MIN, Math.round(number / STEP) * STEP));
};

const scopeLabels = {
  User: "User",
  Department: "Department",
  Role: "Role",
  Designation: "Designation",
};

const scopeHelpers = {
  User: "Choose the specific user to apply this policy to.",
  Department: "Choose the specific department to apply this policy to.",
  Role: "Choose the specific role to apply this policy to.",
};

const stepIconSx = (enabled, color) => ({
  color,
  opacity: enabled ? 1 : 0.3,
  cursor: enabled ? "pointer" : "not-allowed",
});

// The form body. Mounted fresh every time the panel opens, so its defaults
// (and, when editing, the fetched policy) are applied exactly once.
const PolicyForm = ({
  isEdit = false,
  readOnly = false,
  individualPolicyData = [],
  onSaved,
  setSaving,
}) => {
  const {
    setValue,
    handleSubmit,
    register,
    control,
    watch,
    resetField,
    unregister,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    shouldUnregister: true,
    defaultValues: {
      is_thereshold_duration: false,
      threshold_duration: null,
      allow_anywhere_checkin_checkout: false,
      hide_take_break_option_in_mobile_app: false,
      allow_worksession_tracking: false,
      is_linkto_checkin: false,
      auto_start_checkin: false,
      auto_stop_checkout: false,
      is_auto_start_stop_on_break: false,
      data_updated: false,
      manual_start_stop: false,
      scope: "Department",
    },
  });
  const { domain, userDetails, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  const [showAutoCompleteAvathar, setShowAutoCompleteAvathar] = useState(false);
  const [trigger, setTrigger] = useState(true);
  const autoStart = watch("auto_start_checkin");
  const autoStop = watch("auto_stop_checkout");
  const dataUpdate = watch("data_updated");
  const { result: userData } = useFetch(
    `${domain}list/employee?company_id=${topbarCompanyId}&department_id=${defaultAllSelection}&status=Active`,
  );
  const { result: departmentData } = useFetch(
    `${domain}get-department/?user_id=${userDetails.id}&company_id=${topbarCompanyId}`,
  );
  const { result: designation } = useFetch(`${domain}designation/`);

  const { result: roleData } = useFetch(
    `${domain}product-control/roles/summary/`,
  );
  const scope = watch("scope");
  const [tripDistanceThreshold, setTripDistanceThreshold] = useState(20);

  const onSubmit = async (data) => {
    setSaving(true);

    const isEditing = !!individualPolicyData?.id;

    const url = isEditing
      ? `${domain}tracking/trip-monitoring-policies/${individualPolicyData.id}/`
      : `${domain}tracking/trip-monitoring-policies/`;

    const method = isEditing ? "PUT" : "POST";

    const payload = {
      scope_model: data?.scope?.toLowerCase() || "",
      scope_model_id:
        data?.scope === "Department"
          ? data?.department_id
          : data?.scope === "User"
            ? data?.user_id
            : data?.scope === "Role"
              ? data?.role_id
              : data?.scope === "Designation"
                ? data?.designation_id
                : null,
      is_threshold_minutes: data?.is_thereshold_duration,
      threshold_minutes: data?.is_thereshold_duration
        ? data?.threshold_duration?.minute()
        : "00",
      allow_anywhere_checkin_checkout:
        data?.allow_anywhere_checkin_checkout ?? false,
      hide_take_break_option_in_mobile_app:
        data?.hide_take_break_option_in_mobile_app ?? false,
      allow_worksession_tracking: data?.allow_worksession_tracking ?? false,
      is_linked_to_checkin: data?.is_linkto_checkin ?? false,
      is_auto_start_on_checkin: data?.auto_start_checkin ?? false,
      is_auto_stop_on_checkout: data?.auto_stop_checkout ?? false,
      is_auto_start_stop_on_break: data?.is_auto_start_stop_on_break ?? false,
      allow_manual_start_stop: data?.manual_start_stop ?? false,
      trip_distance_threshold: snapDistance(tripDistanceThreshold),
    };

    const response = await customAPI(url, method, payload);

    setSaving(false);
    if (response.ok) {
      showToast("success", response?.responseDetails?.message);
      onSaved();
    } else {
      showToast(
        "error",
        isEditing
          ? "Couldn't update the policy. Please try again."
          : "Couldn't create the policy. Please try again.",
      );
    }
  };

  const handleScope = (event, data) => {
    if (!data) {
      setValue("scope", "");
      return;
    }

    const selectedScope = data.label;
    setValue("scope", selectedScope);

    const scopeFieldsMap = {
      User: [
        "department_name",
        "department_id",
        "designation_name",
        "designation_id",
        "role_name",
        "role_id",
      ],
      Department: [
        "user_name",
        "user_id",
        "user_data",
        "designation_name",
        "designation_id",
        "role_name",
        "role_id",
      ],
      Designation: [
        "user_name",
        "user_id",
        "user_data",
        "department_name",
        "department_id",
        "role_name",
        "role_id",
      ],

      Role: [
        "user_name",
        "user_id",
        "user_data",
        "department_name",
        "department_id",
        "designation_name",
        "designation_id",
      ],
    };

    scopeFieldsMap[selectedScope]?.forEach((field) => {
      resetField(field);
      unregister(field);
    });
  };

  const updateUserName = (_, data) => {
    setValue("user_name", data?.user_name ?? "");
    setValue("user_data", data ?? "");
    setValue("user_id", data?.user_id ?? "");
    setShowAutoCompleteAvathar(false);
  };

  const updateDepartmentName = (_, data) => {
    setValue("department_name", data?.label ?? "");
    setValue("department_id", data?.id ?? "");
  };

  const hanldeDesignation = (_, data) => {
    setValue("designation_name", data?.label ?? "");
    setValue("designation_id", data?.id ?? "");
  };

  const updateRole = (_, data) => {
    setValue("role_name", data?.role_name ?? "");
    setValue("role_id", data?.role_id ?? "");
  };

  useEffect(() => {
    if (isEdit && individualPolicyData && trigger) {
      setValue(
        "scope",
        individualPolicyData?.scope_model
          ? individualPolicyData.scope_model.charAt(0).toUpperCase() +
              individualPolicyData.scope_model.slice(1)
          : "",
      );
      setValue("department_name", individualPolicyData?.scope_name);
      setValue("department_id", individualPolicyData?.scope_model_id);
      setValue("designation_name", individualPolicyData?.scope_name);
      setValue("designation_id", individualPolicyData?.scope_model_id);
      setValue("user_name", individualPolicyData?.scope_name);
      setValue("user_id", individualPolicyData?.scope_model_id);

      setValue("user_data", {
        label: individualPolicyData?.scope_name,
        id: individualPolicyData?.scope_model_id,
      });
      setValue("role_name", individualPolicyData?.scope_name);
      setValue("role_id", individualPolicyData?.scope_model_id);
      setValue(
        "is_thereshold_duration",
        individualPolicyData?.is_threshold_minutes,
      );

      setValue(
        "threshold_duration",
        dayjs()
          .startOf("day")
          .add(individualPolicyData?.threshold_minutes, "minute"),
      );
      setValue(
        "allow_anywhere_checkin_checkout",
        individualPolicyData?.allow_anywhere_checkin_checkout ?? false,
      );
      setValue(
        "hide_take_break_option_in_mobile_app",
        individualPolicyData?.hide_take_break_option_in_mobile_app ?? false,
      );
      setValue(
        "allow_worksession_tracking",
        individualPolicyData?.allow_worksession_tracking ?? false,
      );
      setValue("is_linkto_checkin", individualPolicyData?.is_linked_to_checkin);

      setValue(
        "auto_start_checkin",
        individualPolicyData?.is_auto_start_on_checkin,
      );
      setValue(
        "auto_stop_checkout",
        individualPolicyData?.is_auto_stop_on_checkout,
      );
      setValue(
        "is_auto_start_stop_on_break",
        individualPolicyData?.is_auto_start_stop_on_break,
      );
      setValue(
        "manual_start_stop",
        individualPolicyData?.allow_manual_start_stop,
      );
      setTripDistanceThreshold(
        individualPolicyData?.trip_distance_threshold || 20,
      );
      setTrigger(false);
    }
  }, [
    individualPolicyData,
    individualPolicyData?.allow_manual_start_stop,
    individualPolicyData?.id,
    individualPolicyData?.is_auto_start_on_checkin,
    individualPolicyData?.is_auto_stop_on_checkout,
    individualPolicyData?.is_linked_to_checkin,
    individualPolicyData.scope_model,
    individualPolicyData?.scope_name,
    isEdit,
    setValue,
    trigger,
  ]);

  const distance = Number(tripDistanceThreshold) || 0;
  const canDecrement = distance > MIN;
  const canIncrement = distance < MAX;

  const handleIncrement = () => {
    setTripDistanceThreshold((prev) =>
      Math.min(MAX, snapDistance(prev) + STEP),
    );
  };

  const handleDecrement = () => {
    setTripDistanceThreshold((prev) =>
      Math.max(MIN, snapDistance(prev) - STEP),
    );
  };

  useEffect(() => {
    if ((autoStart || autoStop) && dataUpdate) {
      setValue("manual_start_stop", true);
    }
  }, [autoStart, autoStop, dataUpdate, setValue]);

  // The scope of an existing policy cannot be changed.
  const scopeLockSx = {
    pointerEvents: isEdit ? "none" : "auto",
    opacity: isEdit ? 0.8 : 1,
  };

  return (
    <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)} noValidate>
      {/* A viewer without edit rights gets the same panel, locked. */}
      <Mui.Box
        component="fieldset"
        disabled={readOnly}
        sx={{
          border: 0,
          p: 0,
          m: 0,
          minWidth: 0,
          pointerEvents: readOnly ? "none" : "auto",
        }}
      >
        <Mui.Stack spacing={2.5}>
          <Mui.Box sx={scopeLockSx}>
            <IconFieldBlock
              title="Scope"
              helper="Select the organisational level where this policy applies."
            >
              <CustomAutoComplete
                dataList={scopeName}
                rules={{
                  ...register("scope", {
                    required: "Scope is required",
                  }),
                }}
                selectedValue={watch("scope")}
                updateDataFunction={handleScope}
                placeHolder="Select the scope"
                errorMessage={
                  errors?.scope?.message ? errors?.scope?.message : ""
                }
                textBoxError={errors?.scope ? true : false}
              />
            </IconFieldBlock>
          </Mui.Box>

          <Mui.Box sx={scopeLockSx}>
            <IconFieldBlock
              title={scopeLabels[scope] ?? "Designation"}
              helper={
                scopeHelpers[scope] ??
                "Choose the specific designation to apply this policy to."
              }
            >
              {scope === "User" && (
                <CustomAutoCompleteWithLogo
                  dataList={userData.map((obj) => ({
                    ...obj,
                    label: obj.user_name,
                    image: obj?.image_url,
                  }))}
                  rules={{
                    ...register("user_name", {
                      required:
                        scope === "User" ? "User name is required" : false,
                    }),
                  }}
                  selectedValue={watch("user_data")}
                  updateDataFunction={updateUserName}
                  placeHolder="Select the employee name"
                  showAvathar={showAutoCompleteAvathar}
                  setShowAutoCompleteAvathar={setShowAutoCompleteAvathar}
                  errorMessage={
                    errors?.user_name?.message ? errors?.user_name?.message : ""
                  }
                  textBoxError={errors?.user_name ? true : false}
                />
              )}
              {scope === "Department" && (
                <CustomAutoComplete
                  dataList={departmentData?.map((obj) => ({
                    ...obj,
                    label: obj.department_name,
                  }))}
                  rules={{
                    ...register("department_name", {
                      required:
                        scope === "Department"
                          ? "Department name is required"
                          : false,
                    }),
                  }}
                  selectedValue={watch("department_name") || null}
                  updateDataFunction={updateDepartmentName}
                  placeHolder="Select department name"
                  errorMessage={
                    errors?.department_name?.message
                      ? errors?.department_name?.message
                      : ""
                  }
                  textBoxError={errors?.department_name ? true : false}
                />
              )}
              {scope === "Designation" && (
                <CustomAutoComplete
                  dataList={designation?.map((obj) => ({
                    ...obj,
                    label: obj.designation,
                  }))}
                  rules={{
                    ...register("designation_name", {
                      required:
                        scope === "Designation"
                          ? "Designation is required"
                          : false,
                    }),
                  }}
                  selectedValue={watch("designation_name") || null}
                  updateDataFunction={hanldeDesignation}
                  placeHolder="Select designation name"
                  errorMessage={
                    errors?.designation_name?.message
                      ? errors?.designation_name?.message
                      : ""
                  }
                  textBoxError={errors?.designation_name ? true : false}
                />
              )}

              {scope === "Role" && (
                <CustomAutoComplete
                  dataList={roleData.map((obj) => ({
                    ...obj,
                    label: obj.role_name,
                  }))}
                  selectedValue={watch("role_name")}
                  rules={{
                    ...register("role_name", {
                      required: scope === "Role" ? "Role is required" : false,
                    }),
                  }}
                  updateDataFunction={updateRole}
                  placeHolder="Select the role name"
                  errorMessage={
                    errors?.role_name?.message ? errors?.role_name?.message : ""
                  }
                  textBoxError={errors?.role_name ? true : false}
                />
              )}
            </IconFieldBlock>
          </Mui.Box>

          <Mui.Divider sx={{ borderColor: designColors.slate200 }} />

          <IconFieldBlock
            title="Tracking Rules"
            helper="Configure threshold rules and automatic tracking actions for active work sessions."
          />

          <Mui.Box>
            <PanelFieldText
              title="Trip Distance Threshold"
              helper="Set the minimum distance in metres required to log a new location coordinate."
            />
            <Mui.Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 1 }}
            >
              <Mui.TextField
                fullWidth
                size="small"
                value={tripDistanceThreshold}
                placeholder="Enter distance"
                onChange={(event) =>
                  setTripDistanceThreshold(
                    event.target.value.replace(/[^0-9]/g, "").slice(0, 3),
                  )
                }
                // Typed values settle on the nearest allowed step.
                onBlur={() =>
                  setTripDistanceThreshold((prev) => snapDistance(prev))
                }
                inputProps={{
                  inputMode: "numeric",
                  "aria-label": "Trip Distance Threshold",
                }}
                sx={globalTextFieldSx}
              />
              <Mui.Tooltip
                title={"Decrease by " + STEP}
                arrow
                slotProps={tooltipStyleProps}
              >
                <RemoveCircleOutlineRounded
                  role="button"
                  aria-label="Decrease trip distance threshold"
                  onClick={canDecrement ? handleDecrement : undefined}
                  sx={stepIconSx(canDecrement, "#DC2626")}
                />
              </Mui.Tooltip>
              <Mui.Tooltip
                title={"Increase by " + STEP}
                arrow
                slotProps={tooltipStyleProps}
              >
                <AddCircleOutlineRounded
                  role="button"
                  aria-label="Increase trip distance threshold"
                  onClick={canIncrement ? handleIncrement : undefined}
                  sx={stepIconSx(canIncrement, designColors.tealDark)}
                />
              </Mui.Tooltip>
            </Mui.Stack>
          </Mui.Box>

          <Mui.Box>
            <Mui.Box sx={{ pr: CARD_SWITCH_INSET }}>
              <ToggleRow
                title="Checkpoint Threshold Duration"
                helper="Set the time limit required to log a stationary checkpoint or stop."
                {...register("is_thereshold_duration")}
                checked={Boolean(watch("is_thereshold_duration"))}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setValue("is_thereshold_duration", checked);
                  if (!checked) {
                    setValue("threshold_duration", null);
                  }
                }}
              />
            </Mui.Box>
            {watch("is_thereshold_duration") && (
              <Mui.Stack direction="row" spacing={1.5} alignItems="center">
                <Mui.Box sx={{ flex: 1, minWidth: 0 }}>
                  <CustomTimePicker
                    name="threshold_duration"
                    control={control}
                    errors={errors}
                    validateFn={(value) => {
                      if (!value || !dayjs(value).isValid()) {
                        return "Duration is required";
                      }
                      return true;
                    }}
                    onlyMinutes={true}
                  />
                </Mui.Box>
                <Mui.Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: designColors.slate700,
                  }}
                >
                  Minutes
                </Mui.Typography>
              </Mui.Stack>
            )}
          </Mui.Box>

          <PanelSectionCard icon={<PlaceRounded />} title="Check-in Behaviour">
            <ToggleRow
              isLast={false}
              title="Allow check-in from anywhere"
              helper="Employees can clock in from any location without geographical restrictions."
              {...register("allow_anywhere_checkin_checkout")}
              checked={Boolean(watch("allow_anywhere_checkin_checkout"))}
              onChange={(event) =>
                setValue("allow_anywhere_checkin_checkout", event.target.checked)
              }
            />
            <ToggleRow
              title="Hide Break Option in the Mobile Application"
              helper="Displays the Take Break option in the mobile application after check-in, allowing users to start a break."
              {...register("hide_take_break_option_in_mobile_app")}
              checked={Boolean(watch("hide_take_break_option_in_mobile_app"))}
              onChange={(event) =>
                setValue(
                  "hide_take_break_option_in_mobile_app",
                  event.target.checked,
                )
              }
            />
          </PanelSectionCard>

          <PanelSectionCard
            icon={<AccessTimeRounded />}
            title="Work Session Tracking"
            accent={detailAccentPalette.blue}
          >
            <ToggleRow
              title="Enable Location Tracking"
              helper="Tracks and logs GPS location in the background for the duration of an active work session."
              {...register("allow_worksession_tracking")}
              checked={Boolean(watch("allow_worksession_tracking"))}
              onChange={(event) =>
                setValue("allow_worksession_tracking", event.target.checked)
              }
            />
          </PanelSectionCard>

          {watch("allow_worksession_tracking") && (
            <PanelSectionCard
              icon={<LinkRounded />}
              title="Work Session Automation"
              accent={detailAccentPalette.purple}
            >
              <ToggleRow
                isLast={!watch("is_linkto_checkin")}
                title="Link Work Session Tracking With Punch Entry"
                helper="Links work session tracking directly to attendance punches."
                {...register("is_linkto_checkin")}
                checked={Boolean(watch("is_linkto_checkin"))}
                onChange={(event) =>
                  setValue("is_linkto_checkin", event.target.checked)
                }
              />
              {watch("is_linkto_checkin") && (
                <>
                  <ToggleRow
                    isLast={false}
                    title="Auto Start on Check-in"
                    helper="Starts tracking the work session immediately upon clock-in."
                    {...register("auto_start_checkin")}
                    checked={Boolean(watch("auto_start_checkin"))}
                    onChange={(event) => {
                      setValue("auto_start_checkin", event.target.checked);
                      setValue("data_updated", true);
                    }}
                  />
                  <ToggleRow
                    isLast={false}
                    title="Auto Stop on Check-out"
                    helper="Stops tracking the work session immediately upon clock-out."
                    {...register("auto_stop_checkout")}
                    checked={Boolean(watch("auto_stop_checkout"))}
                    onChange={(event) => {
                      setValue("auto_stop_checkout", event.target.checked);
                      setValue("data_updated", true);
                    }}
                  />
                  <ToggleRow
                    isLast={false}
                    title="Auto Split on Break"
                    helper="Stops active session tracking during scheduled breaks and starts a new session upon return."
                    {...register("is_auto_start_stop_on_break")}
                    checked={Boolean(watch("is_auto_start_stop_on_break"))}
                    onChange={(event) =>
                      setValue(
                        "is_auto_start_stop_on_break",
                        event.target.checked,
                      )
                    }
                  />
                  <ToggleRow
                    title="Allow Manual Start/Stop"
                    helper="Permits employees to manually start or stop their own session tracking."
                    {...register("manual_start_stop")}
                    checked={Boolean(watch("manual_start_stop"))}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      if (
                        (autoStart && !autoStop) ||
                        (!autoStart && autoStop)
                      ) {
                        setValue("manual_start_stop", true);
                      } else {
                        setValue("manual_start_stop", checked);
                      }
                    }}
                  />
                </>
              )}
            </PanelSectionCard>
          )}
        </Mui.Stack>
      </Mui.Box>
    </form>
  );
};

// Fetches the full policy before showing the form. Mounted only while the
// panel is open, so every open reads the latest saved values.
const EditPolicyForm = ({ policyId, ...formProps }) => {
  const { domain } = useSelector((state) => state.tokenReducer);
  const { result: individualPolicyData, loading } = useFetch(
    `${domain}tracking/trip-monitoring-policies/${policyId}/`,
  );
  if (String(individualPolicyData?.id) !== String(policyId)) {
    return loading ? (
      <Loader info="Loading..." />
    ) : (
      <Mui.Typography sx={{ fontSize: 14, color: designColors.slate600 }}>
        Couldn&apos;t load this policy. Please close the panel and try again.
      </Mui.Typography>
    );
  }
  return (
    <PolicyForm isEdit individualPolicyData={individualPolicyData} {...formProps} />
  );
};

// Create / edit side panel. `policy` is the list row being edited, or null
// when creating. Without `canEdit` an existing policy opens locked; the
// Delete icon needs `canDelete` (the approver permission).
export const PolicyPanel = ({ open, policy, canEdit, canDelete = canEdit, onClose, onChanged }) => {
  const { domain } = useSelector((state) => state.tokenReducer);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isEdit = Boolean(policy);
  const readOnly = isEdit && !canEdit;

  const handleSaved = () => {
    onChanged();
    onClose();
  };

  const deletePolicy = async () => {
    if (!canDelete) return;
    setDeleting(true);
    const response = await customAPI(
      `${domain}tracking/trip-monitoring-policies/${policy.id}/`,
      "PATCH",
      { isdeleted: true },
    );
    setDeleting(false);
    setConfirmDelete(false);
    if (response.ok) {
      showToast(
        "success",
        response?.responseDetails?.message || "Policy deleted.",
      );
      onChanged();
      onClose();
    } else {
      showToast("error", "Couldn't delete the policy. Please try again.");
    }
  };

  return (
    <>
      <GlobalSidePanel
        open={open}
        onClose={onClose}
        header={
          <PanelIconHeader
            title={isEdit ? "Check-in Policy" : "New Check-in Policy"}
            subtitle="Review the tracking rules and session behaviour applied to the selected scope."
          />
        }
        bodySx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}
        headerActions={
          isEdit && canDelete ? (
            <Mui.Tooltip title="Delete" arrow slotProps={tooltipStyleProps}>
              <Mui.IconButton
                size="small"
                aria-label="Delete policy"
                onClick={() => setConfirmDelete(true)}
                sx={{ ...designShell.panelSoftIconButton, color: "#DC2626" }}
              >
                <DeleteOutlineRounded fontSize="small" />
              </Mui.IconButton>
            </Mui.Tooltip>
          ) : null
        }
        footer={
          readOnly ? (
            <Mui.Stack direction="row" justifyContent="flex-end">
              <SecondaryButton onClick={onClose}>Close</SecondaryButton>
            </Mui.Stack>
          ) : (
            <PanelActionFooter
              onCancel={onClose}
              primaryLabel={isEdit ? "Update" : "Create"}
              primaryProps={{ type: "submit", form: FORM_ID, loading: saving }}
            />
          )
        }
      >
        {open &&
          (isEdit ? (
            <EditPolicyForm
              key={policy.id}
              policyId={policy.id}
              readOnly={readOnly}
              onSaved={handleSaved}
              setSaving={setSaving}
            />
          ) : (
            <PolicyForm onSaved={handleSaved} setSaving={setSaving} />
          ))}
      </GlobalSidePanel>

      <GlobalConfirmDialog
        open={confirmDelete}
        title="Delete policy"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={deletePolicy}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        loading={deleting}
        loadingLabel="Deleting..."
        danger
      >
        Delete the check-in policy for {policy?.scope_name || "this scope"}?
      </GlobalConfirmDialog>
    </>
  );
};
